/**
 * Central de Projetos: configuração da unidade.
 *
 * Liga e desliga a Central (feature flag), ajusta intervalo da análise,
 * retenção, limite do Assistente e esconde as 17 telas antigas de Projetos
 * Executivos. Guarda também como reconhecer as conversas da unidade: o nome
 * da equipe, os nomes da diretoria e as palavras que não identificam ninguém
 * (cada marcenaria tem os seus; nada disso fica no código). Gera o token do
 * monitor (o valor aparece uma vez) e mostra o QR quando o WhatsApp pede para
 * conectar. Mostra também a cota do Gemini no dia
 * (por fatia) e o custo de IA do mês, numa leitura só do `uso-ia`.
 */
import React, { useEffect, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import BotaoCopiar from "../../components/BotaoCopiar";
import ConfirmDialog from "../../components/ConfirmDialog";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { recarregarEstadoCentral } from "../../hooks/useEstadoCentral";
import {
  gerarTokenMonitor,
  lerConfiguracao,
  lerUsoIA,
  listarConexoesMcp,
  revogarConexaoMcp,
  salvarConfiguracao,
} from "../../services/centralProjetos";
import { formatarQuantidade } from "../../utils/dinheiro";
import { quandoFoi } from "./apresentacao";
import { useCarga } from "./comum";
import PainelGeminiHoje from "./PainelGeminiHoje";
import {
  Acoes,
  Aviso,
  Botao,
  Campo,
  Filtros,
  Item,
  Linha,
  Lista,
  Pagina,
  Principal,
  Secao,
  SecaoTitulo,
  Secundario,
  Titulo,
  Topo,
  Vazio,
} from "./styles";
import { guardarGeminiHoje } from "./useGeminiHoje";

const NUMEROS = [
  { campo: "intervalo_analise_min", rotulo: "Minutos entre mensagem nova e análise", min: 5, max: 1440 },
  { campo: "retencao_texto_dias", rotulo: "Dias com o texto no banco", min: 30, max: 365 },
  { campo: "historico_meses", rotulo: "Meses de histórico arquivado", min: 6, max: 60 },
  { campo: "limite_assistente_dia", rotulo: "Perguntas por dia ao Assistente (0 desliga)", min: 0, max: 500 },
];
/** Limites do backend para as regras de reconhecimento das conversas. */
const TAMANHO_NOME_DA_EQUIPE = 60;
/** Nomes da diretoria: um por linha (vírgula e ponto e vírgula também separam). */
const SEPARA_NOMES = /[\n,;]+/;
/** Palavras ignoradas: separadas por vírgula (ponto e vírgula e quebra de linha também). */
const SEPARA_PALAVRAS = /[,;\n]+/;

/** Texto livre → lista sem vazios nem repetidos (ignora maiúsculas ao comparar). */
function lista(texto, separador) {
  const vistos = new Set();
  return String(texto || "")
    .split(separador)
    .map((item) => item.trim())
    .filter((item) => {
      const chave = item.toLocaleLowerCase("pt-BR");
      if (!item || vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    });
}

/** Resposta da API → formulário: as listas viram o texto que a pessoa edita. */
function paraFormulario(dados) {
  return {
    ...dados,
    nome_da_equipe: dados.nome_da_equipe || "",
    diretoria_texto: (dados.nomes_da_diretoria || []).join("\n"),
    palavras_texto: (dados.palavras_ignoradas || []).join(", "),
  };
}

const ESTADO_MONITOR = {
  INICIANDO: "Iniciando",
  QR_REQUIRED: "Esperando o QR",
  ONLINE: "Lendo",
  DESCONECTADO: "Desconectado",
  ERRO: "Com erro",
};

function UsoDeIA({ dados }) {
  const linhas = dados?.linhas || [];
  return (
    <Secao aria-labelledby="uso-ia">
      <SecaoTitulo><h2 id="uso-ia">Uso de IA no mês</h2></SecaoTitulo>
      <Lista>
        {linhas.length === 0 ? <Vazio>Nenhuma chamada de IA este mês.</Vazio> : null}
        {linhas.map((l) => (
          <Item key={`${l.provedor}-${l.modelo}`}>
            <Linha $colunas="minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1.6fr)">
              <Principal>{l.provedor === "jev" ? "Jev" : "Gemini"} <small>· {l.modelo}</small></Principal>
              <Secundario>{formatarQuantidade(l.chamadas)} chamadas{l.falhas ? ` · ${l.falhas} falharam` : ""}</Secundario>
              <Secundario>{formatarQuantidade(l.tokens_entrada || 0)} tokens de entrada</Secundario>
              <Secundario>
                US$ {Number(l.custo_usd || 0).toFixed(2)}
                {Number(l.custo_se_pago_usd) > Number(l.custo_usd) ? ` (seria US$ ${Number(l.custo_se_pago_usd).toFixed(2)} no plano pago)` : ""}
              </Secundario>
            </Linha>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}

function Monitor({ config, aoMudar }) {
  const { request } = useAuth();
  const [token, setToken] = useState(null);
  const [confirmar, setConfirmar] = useState(false);
  const monitor = config.monitor;

  async function gerar() {
    const resposta = await gerarTokenMonitor(request);
    setToken(resposta.token);
    setConfirmar(false);
    aoMudar();
  }

  return (
    <Secao aria-labelledby="monitor">
      <SecaoTitulo><h2 id="monitor">Monitor do WhatsApp</h2></SecaoTitulo>
      <Lista>
        <Item>
          <Linha>
            <Principal>{monitor ? ESTADO_MONITOR[monitor.estado] || monitor.estado : "Ainda não conectou"}</Principal>
            {monitor ? (
              <Secundario>
                Último sinal {quandoFoi(monitor.ultimo_heartbeat)} · versão {monitor.versao || "—"} · {monitor.fila} na fila
                {monitor.mensagem ? ` · ${monitor.mensagem}` : ""}
              </Secundario>
            ) : null}
            {monitor?.qr_imagem ? (
              <div style={{ marginTop: 8 }}>
                <Aviso $tom="warning">
                  Abra o WhatsApp no celular da equipe, Aparelhos conectados, Conectar um aparelho, e escaneie.
                </Aviso>
                <img src={monitor.qr_imagem} alt="QR para conectar o WhatsApp da equipe" width={228} height={228} style={{ marginTop: 8 }} />
              </div>
            ) : null}
          </Linha>
        </Item>
        <Item>
          <Linha>
            <Principal>Token do agente</Principal>
            <Secundario>
              {config.token
                ? `Ativo: ${config.token.prefixo}… · criado ${quandoFoi(config.token.criado_em)} · último uso ${quandoFoi(config.token.ultimo_uso_em)}`
                : "Nenhum token gerado."}
            </Secundario>
            {token ? (
              <Aviso $tom="warning" style={{ marginTop: 8 }}>
                Copie agora e coloque em CENTRAL_AGENT_TOKEN na VM: este valor não aparece de novo.
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, overflowWrap: "anywhere" }}>
                  <code style={{ userSelect: "all" }}>{token}</code>
                  <BotaoCopiar texto={token} />
                </div>
              </Aviso>
            ) : null}
            <Acoes style={{ marginTop: 6 }}>
              <Botao type="button" onClick={() => setConfirmar(true)}>{config.token ? "Gerar outro token" : "Gerar token"}</Botao>
            </Acoes>
          </Linha>
        </Item>
      </Lista>
      <ConfirmDialog
        open={confirmar}
        titulo={config.token ? "Gerar outro token?" : "Gerar o token do monitor?"}
        descricao={config.token ? "O token atual para de funcionar na hora; o monitor só volta com o novo." : "O token liga o monitor desta unidade à Central."}
        confirmarLabel="Gerar"
        tone="primary"
        onConfirmar={gerar}
        onCancelar={() => setConfirmar(false)}
      />
    </Secao>
  );
}

function ReconhecimentoDasConversas({ form, aoMudar, aoSalvar }) {
  const padrao = form.nome_da_equipe_padrao || "";
  return (
    <Secao aria-labelledby="reconhecimento">
      <SecaoTitulo><h2 id="reconhecimento">Reconhecimento das conversas</h2></SecaoTitulo>
      <Filtros style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", alignItems: "start" }}>
        <div>
          <Campo>
            Nome da equipe
            <input
              type="text"
              maxLength={TAMANHO_NOME_DA_EQUIPE}
              value={form.nome_da_equipe}
              placeholder={padrao}
              aria-describedby="ajuda-nome-equipe"
              onChange={(e) => aoMudar({ nome_da_equipe: e.target.value })}
            />
          </Campo>
          <Secundario $quebra id="ajuda-nome-equipe" style={{ marginTop: 4 }}>
            Conversa com esse nome no título vira grupo da Equipe, e é assim que aparece a mensagem enviada pelo celular
            da equipe.{padrao ? ` Vazio, vale "${padrao}".` : ""}
          </Secundario>
        </div>
        <div>
          <Campo>
            Nomes da diretoria
            <textarea
              rows={3}
              value={form.diretoria_texto}
              aria-describedby="ajuda-diretoria"
              onChange={(e) => aoMudar({ diretoria_texto: e.target.value })}
            />
          </Campo>
          <Secundario $quebra id="ajuda-diretoria" style={{ marginTop: 4 }}>
            Um por linha. Conversa com um desses nomes no título vira Diretoria.
          </Secundario>
        </div>
        <div>
          <Campo>
            Palavras ignoradas
            <input
              type="text"
              value={form.palavras_texto}
              aria-describedby="ajuda-palavras"
              onChange={(e) => aoMudar({ palavras_texto: e.target.value })}
            />
          </Campo>
          <Secundario $quebra id="ajuda-palavras" style={{ marginTop: 4 }}>
            Separadas por vírgula. Palavras que aparecem em nome de grupo e de cliente sem identificar ninguém. As comuns
            (cliente, projeto, obra…) e o nome da equipe já são ignorados.
          </Secundario>
        </div>
      </Filtros>
      <Acoes>
        <AsyncButton variant="primary" successMessage="Configuração salva." onClick={aoSalvar}>Salvar</AsyncButton>
      </Acoes>
    </Secao>
  );
}

function ConectorDoClaude({ url }) {
  const { request } = useAuth();
  const { dados, recarregar } = useCarga((req) => listarConexoesMcp(req), []);
  const conexoes = dados || [];
  return (
    <Secao aria-labelledby="mcp">
      <SecaoTitulo><h2 id="mcp">Conector do Claude (MCP)</h2></SecaoTitulo>
      <Aviso $tom="info">
        No Claude: Configurações, Conectores, Adicionar conector personalizado, e cole o endereço abaixo. O Claude
        abre esta Central para você permitir o acesso (somente leitura).
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, overflowWrap: "anywhere" }}>
          <code style={{ userSelect: "all" }}>{url}</code>
          <BotaoCopiar texto={url} />
        </div>
      </Aviso>
      <Lista>
        {conexoes.length === 0 ? <Vazio>Nenhum Claude conectado.</Vazio> : null}
        {conexoes.map((c) => (
          <Item key={c.id}>
            <Linha $colunas="minmax(0, 2fr) minmax(0, 2fr) auto">
              <Principal>{c.aplicativo} <small>· {c.pessoa}</small></Principal>
              <Secundario>Conectado {quandoFoi(c.criado_em)} · último uso {quandoFoi(c.ultimo_uso_em)}</Secundario>
              <AsyncButton
                variant="secondary"
                successMessage="Acesso revogado."
                onClick={async () => { await revogarConexaoMcp(request, c.id); recarregar(); }}
              >
                Revogar
              </AsyncButton>
            </Linha>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}

export default function ConfiguracaoCentral() {
  const navigate = useNavigate();
  const { request } = useAuth();
  const { dados, erro, carregando, recarregar } = useCarga((req) => lerConfiguracao(req), []);
  const usoIA = useCarga((req) => lerUsoIA(req), []);
  const [form, setForm] = useState(null);

  // A cota lida aqui serve também às outras telas (prévia, Assistente, perfil).
  useEffect(() => {
    guardarGeminiHoje(usoIA.dados?.gemini_hoje);
  }, [usoIA.dados]);

  // Recarga posterior só atualiza o monitor e o token: não apaga o que a
  // pessoa está digitando no formulário.
  useEffect(() => {
    if (dados) setForm((atual) => (atual ? { ...atual, monitor: dados.monitor, token: dados.token } : paraFormulario(dados)));
  }, [dados]);

  // O QR do WhatsApp troca a cada ~20 s: enquanto o monitor pede o QR, a
  // tela busca o novo sozinha.
  const pedeQr = form?.monitor?.estado === "QR_REQUIRED";
  useEffect(() => {
    if (!pedeQr) return undefined;
    const intervalo = setInterval(recarregar, 15000);
    return () => clearInterval(intervalo);
  }, [pedeQr, recarregar]);

  if (carregando && !dados) return <PageLoader scope="central-projetos" />;

  async function salvar() {
    const corpo = {
      ativa: form.ativa,
      esconder_projetos_executivos: form.esconder_projetos_executivos,
      ...Object.fromEntries(NUMEROS.map(({ campo }) => [campo, Number(form[campo])])),
      nome_da_equipe: form.nome_da_equipe.trim(),
      nomes_da_diretoria: lista(form.diretoria_texto, SEPARA_NOMES),
      palavras_ignoradas: lista(form.palavras_texto, SEPARA_PALAVRAS),
    };
    const salvo = await salvarConfiguracao(request, corpo);
    setForm(paraFormulario(salvo));
    await recarregarEstadoCentral(request);
  }

  return (
    <Pagina>
      <Topo>
        <Botao type="button" aria-label="Voltar ao painel" title="Voltar" onClick={() => navigate("/central-projetos")}>
          <FiArrowLeft aria-hidden="true" />
        </Botao>
        <Titulo>
          <h1>Configuração da Central</h1>
          <p>Ligar, prazos da análise, retenção, reconhecimento das conversas e o monitor do WhatsApp.</p>
        </Titulo>
      </Topo>

      {erro ? <Aviso $tom="danger">{erro.message}</Aviso> : null}
      {form ? (
        <>
          <Secao aria-labelledby="geral">
            <SecaoTitulo><h2 id="geral">Geral</h2></SecaoTitulo>
            <Lista>
              <Item>
                <Linha $colunas="minmax(0, 1fr) auto">
                  <div>
                    <Principal>Central de Projetos ligada</Principal>
                    <Secundario $quebra>Desligada, o processo de fundo não lê nada e as telas novas somem do menu.</Secundario>
                  </div>
                  <input type="checkbox" aria-label="Central ligada" checked={form.ativa} onChange={(e) => setForm({ ...form, ativa: e.target.checked })} style={{ width: 20, height: 20 }} />
                </Linha>
              </Item>
              <Item>
                <Linha $colunas="minmax(0, 1fr) auto">
                  <div>
                    <Principal>Esconder as telas antigas de Projetos Executivos</Principal>
                    <Secundario $quebra>As 17 telas leem a planilha de Distribuição, que vai ser abandonada. Escondidas, saem do menu; nada é apagado.</Secundario>
                  </div>
                  <input type="checkbox" aria-label="Esconder telas antigas" checked={form.esconder_projetos_executivos} onChange={(e) => setForm({ ...form, esconder_projetos_executivos: e.target.checked })} style={{ width: 20, height: 20 }} />
                </Linha>
              </Item>
            </Lista>
            <Filtros>
              {NUMEROS.map(({ campo, rotulo, min, max }) => (
                <Campo key={campo}>
                  {rotulo}
                  <input type="number" min={min} max={max} value={form[campo]} onChange={(e) => setForm({ ...form, [campo]: e.target.value })} />
                </Campo>
              ))}
            </Filtros>
            <Acoes>
              <AsyncButton variant="primary" successMessage="Configuração salva." onClick={salvar}>Salvar</AsyncButton>
            </Acoes>
          </Secao>
          <ReconhecimentoDasConversas form={form} aoMudar={(mudanca) => setForm({ ...form, ...mudanca })} aoSalvar={salvar} />
          <Monitor config={form} aoMudar={recarregar} />
          <PainelGeminiHoje gemini={usoIA.dados?.gemini_hoje} carregando={usoIA.carregando} />
          <ConectorDoClaude url={form.mcp_url} />
          <UsoDeIA dados={usoIA.dados} />
        </>
      ) : null}
    </Pagina>
  );
}
